// Client-config override system — see docs/client-config-overrides.md.
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getClientConfig } from 'src/services/clientConfigService';

export const ClientConfigContext = React.createContext({
  config: null,
  isLoading: true,
});

export default function ClientConfigProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Provider mounts on the login screen, before the nsd.token cookie exists, so the
  // fetch is gated on the authenticated user instead of running once on mount.
  const isAuthenticated = useSelector((state) => Boolean(state.account?.user));

  useEffect(() => {
    if (!isAuthenticated) {
      setConfig(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const loadConfig = async () => {
      try {
        const result = await getClientConfig();
        if (isMounted) setConfig(result ?? null);
      } catch {
        // Fail open: any rejection keeps config null so downstream uses defaults.
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  return (
    <ClientConfigContext.Provider value={{ config, isLoading }}>
      {children}
    </ClientConfigContext.Provider>
  );
}

ClientConfigProvider.propTypes = {
  children: PropTypes.node,
};
